import { ReactComponent as ActionDeleteIcon } from '@epam/assets/icons/common/action-delete-18.svg';
import {
  Button,
  Checkbox,
  FlexCell,
  MultiSwitch,
  Panel,
  SearchInput,
} from '@epam/promo';
import { Tooltip } from '@epam/uui';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';

import { getUsers } from 'api/users.service';
import { UserManagementCard } from 'components/userManagement';
import { useModal, useScroll, useUserDeleteQuery } from 'hooks';
import { useUserRole } from 'hooks/helpers';
import { BotUsers } from 'models/user.model';
import css from './UserManagement.module.scss';

const USERS_FOR_DELETION_TOOLTIP = `This tab displays users whose emails are no longer associated with EPAM.
  It is advisable to remove them from the application.`;

enum SwitcherId {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export const UserManagement = () => {
  const { isSuperAdmin } = useUserRole();
  const containerRef = useRef<HTMLDivElement>(null);
  const { isLoading, data: users } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
    select: useCallback(
      (data: BotUsers[]) =>
        data.reduce<Record<SwitcherId, string[]>>(
          (acc, user) => {
            if (user.isActive) {
              acc[SwitcherId.ACTIVE].push(user.email);
            } else {
              acc[SwitcherId.INACTIVE].push(user.email);
            }

            return acc;
          },
          {
            [SwitcherId.ACTIVE]: [],
            [SwitcherId.INACTIVE]: [],
          },
        ),
      [],
    ),
  });

  const [selected, setSelected] = useState<Record<SwitcherId, string[]>>({
    [SwitcherId.ACTIVE]: [],
    [SwitcherId.INACTIVE]: [],
  });
  const [search, setSearch] = useState('');
  const [switcher, setSwitcher] = useState(SwitcherId.ACTIVE);
  const { isScrollable } = useScroll(
    containerRef,
    users ? [users[switcher].length] : [],
  );
  const modal = useModal();
  const { mutate: deleteUsers } = useUserDeleteQuery();

  const hasAdminControls = useMemo(
    () => isSuperAdmin && !!users?.[switcher].length,
    [isSuperAdmin, users, switcher],
  );

  if (!users) {
    return (
      <Panel
        shadow
        background="white"
        cx={{
          [css.page__panel]: true,
          'skeleton-loader': isLoading,
        }}
      />
    );
  }

  const selectAll =
    !!users[switcher].length &&
    users[switcher].length === selected[switcher].length;

  const setSelectAll = () => {
    setSelected({
      ...selected,
      [switcher]: selectAll
        ? []
        : [
            ...(search
              ? users[switcher].filter((email) =>
                  email
                    .toLocaleLowerCase()
                    .includes(search.toLocaleLowerCase()),
                )
              : users[switcher]),
          ],
    });
  };

  const handleSelect = (email: string, value: boolean) => {
    setSelected(
      value
        ? { ...selected, [switcher]: [...selected[switcher], email] }
        : {
            ...selected,
            [switcher]: selected[switcher].filter((item) => item !== email),
          },
    );
  };

  const removeSelected = () =>
    modal.delete({
      text: `Are you certain you want to proceed with the deletion?
      The user(s) listed below will no longer have access to the chatbot.`,
      highlights: ['the deletion', 'will no longer have access'],
      onSuccess: () => {
        deleteUsers(selected[switcher]);
        setSelected({
          ...selected,
          [switcher]: [],
        });
      },
    });

  const handleSingleDelete = (email: string) =>
    modal.delete({
      text: `Are you sure you want to delete ${email}?`,
      highlights: [email, 'delete', 'revoke'],
      onSuccess: () => {
        deleteUsers([email]);
        setSelected({
          ...selected,
          [switcher]: selected[switcher].filter((e) => e !== email),
        });
      },
    });

  return (
    <Panel
      shadow
      background="white"
      cx={{
        [css.page__panel]: true,
        [css['page__no-gutter']]: users[switcher].length >= 5,
        'skeleton-loader': isLoading,
      }}>
      <FlexCell cx={css.searchField} width="auto">
        <SearchInput
          debounceDelay={1000}
          placeholder="Search by e-mail"
          value={search}
          onValueChange={(value) => setSearch(value ?? '')}
        />
      </FlexCell>
      <FlexCell
        cx={hasAdminControls ? css.actionContainer : css.singleActionContainer}
        shrink={3}
        width="auto">
        {hasAdminControls && (
          <Checkbox value={selectAll} onValueChange={setSelectAll} />
        )}
        <MultiSwitch
          items={[
            { id: SwitcherId.ACTIVE, caption: 'Active users' },
            {
              id: SwitcherId.INACTIVE,
              caption: (
                <Tooltip content={USERS_FOR_DELETION_TOOLTIP} placement="top">
                  <div>Users for deletion</div>
                </Tooltip>
              ),
            },
          ]}
          value={switcher}
          onValueChange={setSwitcher}
        />
        {hasAdminControls && (
          <Button
            caption="REMOVE SELECTED"
            cx={
              selected[switcher].length
                ? css['remove-button show']
                : css['remove-button']
            }
            fill="light"
            icon={ActionDeleteIcon}
            isDisabled={!selected[switcher].length}
            onClick={removeSelected}
          />
        )}
      </FlexCell>
      <FlexCell
        ref={containerRef}
        cx={{
          [css['page__user-list']]: true,
          [css['page__gutter-top']]: true,
          [css.page__gradient]: isScrollable,
        }}>
        {users[switcher]
          .filter((email) =>
            email.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
          )
          .map((email) => {
            const isSelected = selected[switcher].includes(email);

            return (
              <UserManagementCard
                key={email}
                handleDelete={() => handleSingleDelete(email)}
                handleSelect={() => handleSelect(email, !isSelected)}
                selected={isSelected}
                userEmail={email}
              />
            );
          })}
      </FlexCell>
    </Panel>
  );
};
